function normalize(value, fallback = '') {
  if (value == null || value === '') return fallback;
  return String(value).toLowerCase();
}

export function isBusinessAccount(user) {
  return normalize(user?.account_type, 'individual') === 'business';
}

export function isStaffUser(user) {
  return normalize(user?.role, '') === 'staff' || !!user?.is_staff;
}

export function hasPremiumFeature(user, featureKey) {
  const premium = user?.premium || user || {};
  return !!(premium?.[featureKey] ?? premium?.[featureKey.replace(/_/g, '')] ?? user?.[featureKey]);
}

export function getStaffPermissions(user) {
  if (!isStaffUser(user)) return {};
  const permissions = user?.staff_permissions || {};
  return Object.fromEntries(Object.entries(permissions).map(([key, value]) => [key, !!value]));
}

export function staffHasPermission(user, permission) {
  if (!isStaffUser(user)) return true;
  return !!getStaffPermissions(user)?.[permission];
}

const STAFF_ROUTE_RULES = [
  { prefix: '/dashboard/payment-links', permissions: ['create_payment_links', 'view_payments'] },
  { prefix: '/payment-links', permissions: ['create_payment_links', 'view_payments'] },
  { prefix: '/dashboard/whatsapp-templates', permissions: ['create_payment_links', 'view_payments'] },
  { prefix: '/dashboard/bulk-payment-links', permissions: ['create_payment_links'] },
  { prefix: '/dashboard/transactions', permissions: ['view_payments'] },
  { prefix: '/transactions', permissions: ['view_payments'] },
  { prefix: '/dashboard/withdrawals', permissions: ['withdraw_funds'] },
  { prefix: '/withdrawals', permissions: ['withdraw_funds'] },
  { prefix: '/dashboard/bank-details', permissions: ['withdraw_funds'] },
  { prefix: '/bank-details', permissions: ['withdraw_funds'] },
  { prefix: '/dashboard/settlement', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/verification', permissions: ['__deny_staff__'] },
  { prefix: '/kyc', permissions: ['__deny_staff__'] },
  { prefix: '/dashboard/api-keys', permissions: ['edit_gateway_settings'] },
  { prefix: '/api-keys', permissions: ['edit_gateway_settings'] },
  { prefix: '/dashboard/customers', permissions: ['manage_customers'] },
  { prefix: '/dashboard/crm', permissions: ['manage_customers'] },
  { prefix: '/dashboard/invoices', permissions: ['create_invoices'] },
  { prefix: '/dashboard/quotations', permissions: ['create_invoices'] },
  { prefix: '/dashboard/receipts', permissions: ['view_payments'] },
  { prefix: '/dashboard/reminders', permissions: ['create_invoices', 'manage_customers'] },
  { prefix: '/dashboard/subscriptions', permissions: ['create_invoices', 'manage_customers'] },
  { prefix: '/dashboard/installments', permissions: ['create_invoices', 'manage_customers'] },
  { prefix: '/dashboard/business-profile', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/budget', permissions: ['view_analytics'] },
  { prefix: '/dashboard/analytics', permissions: ['view_analytics'] },
  { prefix: '/dashboard/qr-codes', permissions: ['view_payments', 'create_payment_links'] },
  { prefix: '/dashboard/store/products', permissions: ['manage_products', 'manage_store_settings'] },
  { prefix: '/dashboard/store/orders', permissions: ['manage_orders', 'manage_store_settings'] },
  { prefix: '/dashboard/store/settings', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/store/domains', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/custom-domains', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/delivery-options', permissions: ['manage_orders', 'manage_store_settings'] },
  { prefix: '/dashboard/marketplace', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/remove-branding', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/store', permissions: ['manage_store_settings', 'manage_products', 'manage_orders'] },
  { prefix: '/dashboard/pos', permissions: ['use_pos'] },
  { prefix: '/dashboard/vcard', permissions: ['manage_store_settings'] },
  { prefix: '/dashboard/package', permissions: [] },
  { prefix: '/dashboard/profile', permissions: [] },
  { prefix: '/dashboard/notifications', permissions: [] },
  { prefix: '/dashboard/documentation', permissions: [] },
];

export function canAccessDashboardRoute(user, pathname = '') {
  if (!isStaffUser(user)) return true;
  const normalizedPath = String(pathname || '').toLowerCase();
  const rule = STAFF_ROUTE_RULES.find((item) => normalizedPath === item.prefix || normalizedPath.startsWith(`${item.prefix}/`));
  if (!rule) return true;
  if (!rule.permissions || rule.permissions.length === 0) return true;
  return rule.permissions.some((permission) => staffHasPermission(user, permission));
}

export function isApprovedAccount(user) {
  const status = normalize(user?.account_status, '');
  const verification = normalize(user?.verification_status, '');
  return status === 'approved' || verification === 'verified' || normalize(user?.role, '') === 'verified' || normalize(user?.role, '') === 'admin';
}

export function canCreatePaymentLinks(user) {
  if (normalize(user?.role, '') === 'admin') return true;
  const accountType = normalize(user?.account_type, 'individual');
  if (accountType === 'individual') return isApprovedAccount(user);
  if (!isApprovedAccount(user)) return false;
  if (user?.payment_link_access_enabled == null) return isApprovedAccount(user);
  return !!user.payment_link_access_enabled;
}

export function canUseApiKeys(user) {
  if (normalize(user?.role, '') === 'admin') return true;
  if (!isBusinessAccount(user)) return false;
  if (!isApprovedAccount(user)) return false;
  if (user?.api_access_enabled == null) return isApprovedAccount(user) && isBusinessAccount(user);
  return !!user.api_access_enabled;
}

export function canUseWithdrawals(user) {
  if (normalize(user?.role, '') === 'admin') return true;
  if (!isApprovedAccount(user)) return false;
  if (user?.withdrawals_enabled == null) return isApprovedAccount(user);
  return !!user.withdrawals_enabled;
}

export function canUseLiveMode(user) {
  if (normalize(user?.role, '') === 'admin') return true;
  if (!isApprovedAccount(user)) return false;
  if (user?.live_mode_enabled == null) return isApprovedAccount(user);
  return !!user.live_mode_enabled;
}

export function apiAccessMessage(user) {
  return isBusinessAccount(user)
    ? 'Your business account must be verified before you can generate API keys.'
    : 'API access is available for verified business accounts only.';
}

export function paymentLinkMessage() {
  return 'Your account must be verified before you can create payment links.';
}

export function withdrawalMessage() {
  return 'Your account must be approved before you can request withdrawals.';
}
